import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, User, Download, FileText, RefreshCw } from 'lucide-react';
import { WORKFLOW_STEPS, computeWorkflowTracking } from '@/utils/workflowSteps';
import { STATUS_CFG, PLAN_STATUS } from '@/utils/statusConfig';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

import SummaryStats from './tracking/SummaryStats';
import TrackingRow from './tracking/TrackingRow';
import EstablishmentRow from './tracking/EstablishmentRow';
import ClientReport from './ClientReport';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Tracking = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  const [data, setData] = useState([]);
  const [workPlans, setWorkPlans] = useState([]);
  const [compTxs, setCompTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState('all');
  const [smartFilter, setSmartFilter] = useState('all');
  const [customOrder, setCustomOrder] = useState([]);
  const [showTrackingReport, setShowTrackingReport] = useState(false);
  const isRTL = i18n.language === 'ar';
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [trackRes, planRes, compTxRes, orderRes] = await Promise.all([
        axios.get(`${API}/tracking?show_all=true`, { headers }),
        axios.get(`${API}/work-plans`, { headers }),
        axios.get(`${API}/company-transactions`, { headers }),
        axios.get(`${API}/tracking/order`, { headers }),
      ]);
      setData(trackRes.data.employees || []);
      setWorkPlans(planRes.data.plans || []);
      setCompTxs(compTxRes.data || []);
      setCustomOrder(orderRes.data.order || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [token]);

  // --- Handlers ---
  const handleStatusChange = async (transactionId, newStatus) => {
    if (newStatus === 'cancelled') {
      if (!window.confirm(isRTL ? 'إلغاء المعاملة؟ سيتم إرجاع المبلغ إلى رصيد العميل وتحديث خطة العمل.' : 'Cancel this transaction? The amount will be refunded and the work plan updated.')) return;
      try {
        await axios.put(`${API}/sub-transactions/${transactionId}/cancel`, {}, { headers });
        toast.success(isRTL ? 'تم إلغاء المعاملة وإرجاع المبلغ' : 'Cancelled & refunded');
        await fetchData();
      } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ في الإلغاء' : 'Error cancelling')); }
      return;
    }
    // Require actual_paid_amount before marking as completed
    if (newStatus === 'completed') {
      let tx = null;
      for (const emp of data) {
        tx = emp.transactions.find(t => t.id === transactionId);
        if (tx) break;
      }
      if (tx && (tx.actual_paid_amount === undefined || tx.actual_paid_amount === null || tx.actual_paid_amount === '')) {
        toast.error(isRTL ? 'يجب إدخال المبلغ الفعلي المدفوع أولاً (حتى لو 0)' : 'Please enter the actual paid amount first (even if 0)');
        return;
      }
    }
    try {
      await axios.put(`${API}/sub-transactions/${transactionId}`, { status: newStatus }, { headers });
      let empId = null, subType = null;
      for (const emp of data) {
        const tx = emp.transactions.find(t => t.id === transactionId);
        if (tx) { empId = emp.employee_id; subType = tx.sub_type; break; }
      }
      setData(prev => prev.map(emp => ({
        ...emp,
        transactions: emp.transactions.map(tx =>
          tx.id === transactionId ? { ...tx, status: newStatus } : tx
        )
      })));
      if (empId && subType) {
        const planStatusMap = { completed: 'completed', pending: 'executing', waiting_approval: 'awaiting_approval' };
        const stepStatus = planStatusMap[newStatus] || 'executing';
        setWorkPlans(prev => prev.map(p => {
          if (p.employee_id !== empId) return p;
          return { ...p, steps: (p.steps || []).map(s => s.sub_type === subType ? { ...s, status: stepStatus } : s) };
        }));
      }
      toast.success(isRTL ? 'تم تحديث الحالة' : 'Status updated');
    } catch (e) { toast.error(isRTL ? 'خطأ في تحديث الحالة' : 'Error updating status'); }
  };

  const handleUpdateActualPaid = async (transactionId, amount) => {
    try {
      await axios.put(`${API}/sub-transactions/${transactionId}`, { actual_paid_amount: amount }, { headers });
      setData(prev => prev.map(emp => ({
        ...emp,
        transactions: emp.transactions.map(tx =>
          tx.id === transactionId ? { ...tx, actual_paid_amount: amount } : tx
        )
      })));
      toast.success(isRTL ? 'تم تحديث المبلغ المدفوع' : 'Actual paid updated');
    } catch (e) { toast.error(isRTL ? 'خطأ في التحديث' : 'Error updating'); }
  };

  const handleExecuteStep = async (planId, stepId) => {
    try {
      const res = await axios.post(`${API}/work-plans/${planId}/execute-step/${stepId}`, {}, { headers });
      const newTx = res.data.transaction;
      setWorkPlans(prev => prev.map(p => p.id === planId ? { ...p, steps: p.steps.map(s => s.id === stepId ? { ...s, status: 'executing', transaction_id: newTx?.id } : s) } : p));
      if (newTx?.employee_id) {
        setData(prev => prev.map(emp => emp.employee_id === newTx.employee_id ? { ...emp, transactions: [...emp.transactions, newTx] } : emp));
      }
      if (newTx && !newTx.employee_id) {
        setCompTxs(prev => [...prev, newTx]);
      }
      toast.success(isRTL ? 'تم بدء تنفيذ الخطوة' : 'Step execution started');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleCancelRemaining = async (employeeId, workflowKey, stepsToCancel) => {
    if (!stepsToCancel.length) return;
    try {
      await axios.post(`${API}/tracking/cancel-remaining`, {
        employee_id: employeeId, workflow_key: workflowKey, steps_to_cancel: stepsToCancel,
      }, { headers });
      setData(prev => prev.map(emp => {
        if (emp.employee_id !== employeeId) return emp;
        return { ...emp, transactions: emp.transactions.map(tx =>
          stepsToCancel.some(sc => sc.main_type === tx.main_type && sc.sub_type === tx.sub_type) && tx.status !== 'completed'
            ? { ...tx, status: 'cancelled' } : tx
        )};
      }));
      toast.success(isRTL ? `تم إلغاء ${stepsToCancel.length} خطوة` : `Cancelled ${stepsToCancel.length} steps`);
    } catch (e) { toast.error(isRTL ? 'خطأ في الإلغاء' : 'Error cancelling'); }
  };

  const [reconciling, setReconciling] = useState(false);
  const handleReconcile = async () => {
    if (!window.confirm(isRTL ? 'مزامنة حالات خطوات الخطط مع المعاملات الفعلية، وإقفال الخطط المنتهية، وحذف معاملات المنشأة الملغية نهائياً. متابعة؟' : 'Sync plan steps with live transactions, close finished plans and purge cancelled company transactions. Continue?')) return;
    setReconciling(true);
    try {
      const r = await axios.post(`${API}/work-plans/reconcile`, {}, { headers });
      const d = r.data || {};
      toast.success(isRTL ? `تم: ${d.steps_fixed || 0} خطوة صُحّحت · ${d.plans_closed || 0} خطة أُقفلت · ${d.company_tx_purged || 0} معاملة منشأة حُذفت` : `Done: ${d.steps_fixed || 0} steps fixed · ${d.plans_closed || 0} plans closed · ${d.company_tx_purged || 0} company tx purged`);
      await fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ' : 'Error')); }
    setReconciling(false);
  };

  const handleUpdateNote = async (planId, newNote) => {
    try {
      await axios.put(`${API}/work-plans/${planId}`, { notes: newNote }, { headers });
      setWorkPlans(prev => prev.map(p => p.id === planId ? { ...p, notes: newNote } : p));
      toast.success(isRTL ? 'تم تحديث الملاحظة' : 'Note updated');
    } catch (e) { toast.error(isRTL ? 'خطأ في التحديث' : 'Error updating'); }
  };

  const handleDeletePlan = async (planId) => {
    // SAFETY: NEVER delete a plan from tracking notes - only clear the note text
    try {
      await axios.put(`${API}/work-plans/${planId}`, { notes: '' }, { headers });
      setWorkPlans(prev => prev.map(p => p.id === planId ? { ...p, notes: '' } : p));
      toast.success(isRTL ? 'تم حذف الملاحظة' : 'Note deleted');
    } catch (e) { toast.error(isRTL ? 'خطأ' : 'Error'); }
  };

  // --- Computed Data ---
  const compTxMap = {};
  compTxs.forEach(tx => { compTxMap[tx.id] = tx; });

  const plansByEmp = {};
  const plansByCompany = {};
  workPlans.forEach(p => {
    if (p.plan_type === 'company' && p.company_id) {
      if (!plansByCompany[p.company_id]) plansByCompany[p.company_id] = { company_id: p.company_id, company_name: p.company_name || '', client_name: p.client_name || '', plans: [] };
      plansByCompany[p.company_id].plans.push(p);
      return;
    }
    if (!p.employee_id) return;
    if (!plansByEmp[p.employee_id]) plansByEmp[p.employee_id] = [];
    plansByEmp[p.employee_id].push(p);
  });

  const filtered = data.filter(emp => {
    if (search.trim() && !emp.employee_name.includes(search.trim())) return false;
    if (selectedClient !== 'all' && emp.client_name !== selectedClient) return false;
    if (smartFilter !== 'all') {
      const empPlans = plansByEmp[emp.employee_id] || [];
      const empWorkflows = computeWorkflowTracking(emp.transactions);
      if (smartFilter === 'executing') {
        const hasExecutingPlan = empPlans.some(p => p.steps?.some(s => s.status === 'executing' || s.status === 'awaiting_approval'));
        const hasExecutingWf = empWorkflows.some(wf => wf.steps.some(s => s.status === 'in_progress' || s.status === 'pending' || s.status === 'waiting_approval'));
        return hasExecutingPlan || hasExecutingWf;
      }
      if (smartFilter === 'awaiting_approval') {
        const hasApprovalPlan = empPlans.some(p => p.steps?.some(s => s.status === 'awaiting_approval'));
        const hasApprovalWf = empWorkflows.some(wf => wf.steps.some(s => s.status === 'waiting_approval'));
        return hasApprovalPlan || hasApprovalWf;
      }
      if (smartFilter === 'completed') {
        const hasCompletedPlan = empPlans.some(p => p.steps?.some(s => s.status === 'completed'));
        const hasCompletedWf = empWorkflows.some(wf => wf.steps.some(s => s.status === 'completed'));
        return hasCompletedPlan || hasCompletedWf;
      }
    }
    return true;
  });

  const estEntries = Object.values(plansByCompany).filter(est => {
    if (search.trim() && !est.company_name.includes(search.trim())) return false;
    if (selectedClient !== 'all' && est.client_name !== selectedClient) return false;
    if (smartFilter !== 'all') {
      const allSteps = est.plans.flatMap(p => p.steps || []);
      if (smartFilter === 'executing') return allSteps.some(s => s.status === 'executing' || s.status === 'awaiting_approval');
      if (smartFilter === 'awaiting_approval') return allSteps.some(s => s.status === 'awaiting_approval');
      if (smartFilter === 'completed') return allSteps.some(s => s.status === 'completed');
    }
    return true;
  });

  const clientNames = [...new Set([
    ...data.map(e => e.client_name).filter(Boolean),
    ...Object.values(plansByCompany).map(est => est.client_name).filter(Boolean),
  ])].sort();

  const sorted = [...filtered].sort((a, b) => {
    // Priority: active steps first, completed last
    const getEmpPriority = (emp) => {
      const empPlans = plansByEmp[emp.employee_id] || [];
      const txs = emp.transactions || [];
      const hasExecPlan = empPlans.some(p => p.steps?.some(s => s.status === 'executing' || s.status === 'awaiting_approval'));
      const hasActiveTx = txs.some(t => t.status === 'in_progress' || t.status === 'pending' || t.status === 'waiting_approval');
      if (hasExecPlan || hasActiveTx) return 0; // TOP: active/in-progress

      const allPlanSteps = empPlans.flatMap(p => p.steps || []);
      const allDone = (allPlanSteps.length > 0 || txs.length > 0) &&
        allPlanSteps.every(s => s.status === 'completed' || s.status === 'cancelled') &&
        txs.every(t => t.status === 'completed' || t.status === 'cancelled');
      if (allDone) return 2; // BOTTOM: all completed

      return 1; // DEFAULT
    };

    const getLatestDate = (emp) => {
      const dates = (emp.transactions || []).map(t => t.work_date || t.updated_at || '').filter(Boolean);
      return dates.length > 0 ? dates.sort().reverse()[0] : '';
    };

    const aPriority = getEmpPriority(a);
    const bPriority = getEmpPriority(b);
    if (aPriority !== bPriority) return aPriority - bPriority;

    // For active employees (priority 0): always sort by newest date first
    if (aPriority === 0) {
      const aDate = getLatestDate(a);
      const bDate = getLatestDate(b);
      if (aDate !== bDate) return aDate > bDate ? -1 : 1;
    }

    // Within same priority: use custom order
    const aIdx = customOrder.indexOf(a.employee_id);
    const bIdx = customOrder.indexOf(b.employee_id);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;

    // Fallback: sort by most recent date
    const aDate = getLatestDate(a);
    const bDate = getLatestDate(b);
    if (aDate !== bDate) return aDate > bDate ? -1 : 1;
    return 0;
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sorted.findIndex(e => e.employee_id === active.id);
    const newIndex = sorted.findIndex(e => e.employee_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(sorted, oldIndex, newIndex);
    const newOrder = reordered.map(e => e.employee_id);
    setCustomOrder(newOrder);
    axios.put(`${API}/tracking/order`, { order: newOrder }, { headers }).catch(() => {});
  }, [sorted, headers]);

  // Stats - count unique employees/establishments per filter
  const statCounts = { all: 0, executing: 0, awaiting_approval: 0, completed: 0 };
  data.forEach(emp => {
    const empPlans = plansByEmp[emp.employee_id] || [];
    const empWorkflows = computeWorkflowTracking(emp.transactions);
    if (empWorkflows.length === 0 && empPlans.length === 0) return;
    statCounts.all++;
    const hasExecPlan = empPlans.some(p => p.steps?.some(s => s.status === 'executing' || s.status === 'awaiting_approval'));
    const hasExecWf = empWorkflows.some(wf => wf.steps.some(s => s.status === 'in_progress' || s.status === 'pending' || s.status === 'waiting_approval'));
    if (hasExecPlan || hasExecWf) statCounts.executing++;
    const hasApprovalPlan = empPlans.some(p => p.steps?.some(s => s.status === 'awaiting_approval'));
    const hasApprovalWf = empWorkflows.some(wf => wf.steps.some(s => s.status === 'waiting_approval'));
    if (hasApprovalPlan || hasApprovalWf) statCounts.awaiting_approval++;
    const hasCompPlan = empPlans.some(p => p.steps?.some(s => s.status === 'completed'));
    const hasCompWf = empWorkflows.some(wf => wf.steps.some(s => s.status === 'completed'));
    if (hasCompPlan || hasCompWf) statCounts.completed++;
  });
  Object.values(plansByCompany).forEach(est => {
    const allSteps = est.plans.flatMap(p => p.steps || []);
    statCounts.all++;
    if (allSteps.some(s => s.status === 'executing' || s.status === 'awaiting_approval')) statCounts.executing++;
    if (allSteps.some(s => s.status === 'awaiting_approval')) statCounts.awaiting_approval++;
    if (allSteps.some(s => s.status === 'completed')) statCounts.completed++;
  });

  // Export
  const handleExport = () => {
    const rows = [];
    sorted.forEach(emp => {
      const workflows = computeWorkflowTracking(emp.transactions);
      workflows.forEach(wf => {
        wf.steps.forEach(step => {
          rows.push({
            [isRTL ? 'النوع' : 'Type']: isRTL ? 'موظف' : 'Employee',
            [isRTL ? 'الاسم' : 'Name']: emp.employee_name,
            [isRTL ? 'الشركة' : 'Company']: emp.company_name,
            [isRTL ? 'العميل' : 'Client']: emp.client_name || '',
            [isRTL ? 'نوع المعاملة' : 'Workflow']: isRTL ? wf.label.ar : wf.label.en,
            [isRTL ? 'الخطوة' : 'Step']: isRTL ? step.ar : step.en,
            [isRTL ? 'الحالة' : 'Status']: isRTL ? (STATUS_CFG[step.status === 'in_progress' ? 'pending' : step.status]?.ar || step.status) : (STATUS_CFG[step.status === 'in_progress' ? 'pending' : step.status]?.en || step.status),
            [isRTL ? 'السعر' : 'Price']: step.transaction?.price || 0,
            ...(isAdmin ? { [isRTL ? 'المدفوع' : 'Paid']: step.transaction?.actual_paid_amount || 0 } : {}),
          });
        });
      });
    });
    estEntries.forEach(est => {
      est.plans.forEach(plan => {
        plan.steps?.forEach(step => {
          const tx = step.transaction_id ? compTxMap[step.transaction_id] : null;
          rows.push({
            [isRTL ? 'النوع' : 'Type']: isRTL ? 'منشأة' : 'Establishment',
            [isRTL ? 'الاسم' : 'Name']: est.company_name,
            [isRTL ? 'الشركة' : 'Company']: est.company_name,
            [isRTL ? 'العميل' : 'Client']: est.client_name || '',
            [isRTL ? 'نوع المعاملة' : 'Workflow']: isRTL ? (WORKFLOW_STEPS[step.main_type]?.ar || '') : (WORKFLOW_STEPS[step.main_type]?.en || ''),
            [isRTL ? 'الخطوة' : 'Step']: isRTL ? step.label_ar : step.label_en,
            [isRTL ? 'الحالة' : 'Status']: isRTL ? (PLAN_STATUS[step.status]?.ar || step.status) : (PLAN_STATUS[step.status]?.en || step.status),
            [isRTL ? 'السعر' : 'Price']: tx?.price || step.price || 0,
            ...(isAdmin ? { [isRTL ? 'المدفوع' : 'Paid']: tx?.actual_paid_amount || 0 } : {}),
          });
        });
      });
    });
    if (rows.length === 0) { toast.error(isRTL ? 'لا توجد بيانات للتصدير' : 'No data to export'); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'المتابعة' : 'Tracking');
    const clientSuffix = selectedClient !== 'all' ? `_${selectedClient}` : '';
    XLSX.writeFile(wb, `tracking${clientSuffix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(isRTL ? 'تم تصدير الملف' : 'File exported');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-slate-300 border-t-[#D4AF37] rounded-full" /></div>;
  if (showTrackingReport) return <ClientReport isRTL={isRTL} headers={headers} onClose={() => setShowTrackingReport(false)} showPrices={true} />;

  return (
    <div className="space-y-4" data-testid="tracking-page" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Summary Stats Bar */}
      <SummaryStats isRTL={isRTL} dataCount={statCounts.all}
        executingStepsCount={statCounts.executing} completedStepsCount={statCounts.completed}
        awaitingApprovalStepsCount={statCounts.awaiting_approval}
        smartFilter={smartFilter} setSmartFilter={setSmartFilter} />

      {/* Search + Client Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        {clientNames.length > 1 && (
          <Select value={selectedClient} onValueChange={setSelectedClient} dir={isRTL ? 'rtl' : 'ltr'}>
            <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="client-filter">
              <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <SelectValue placeholder={isRTL ? 'كل العملاء' : 'All Clients'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isRTL ? 'كل العملاء' : 'All Clients'}</SelectItem>
              {clientNames.map(name => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="relative flex-1 max-w-sm">
          <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 ${isRTL ? 'right-3' : 'left-3'}`} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={isRTL ? 'بحث...' : 'Search...'}
            className={`h-9 text-sm ${isRTL ? 'pr-9' : 'pl-9'}`} data-testid="tracking-search" />
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} className="h-9 gap-1.5 shrink-0" data-testid="export-tracking-btn">
          <Download className="w-3.5 h-3.5" />
          {isRTL ? 'تصدير Excel' : 'Export Excel'}
        </Button>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={handleReconcile} disabled={reconciling} className="h-9 gap-1.5 shrink-0 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="reconcile-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${reconciling ? 'animate-spin' : ''}`} />
            {isRTL ? 'تصحيح الحالات العالقة' : 'Fix stuck statuses'}
          </Button>
        )}
      </div>

      {/* Table */}
      {(sorted.length === 0 && estEntries.length === 0) ? (
        <Card className="p-10 text-center"><p className="text-slate-400">{isRTL ? 'لا توجد معاملات' : 'No transactions'}</p></Card>
      ) : (
        <Card className="bg-white border-slate-200 shadow-sm rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
            {isAdmin && <div className="w-5 shrink-0" />}
            <div className="w-3.5 shrink-0" />
            <div className="min-w-[160px] shrink-0">{isRTL ? 'الاسم' : 'Name'}</div>
            <div className="flex-1">{isRTL ? 'النوع' : 'Type'}</div>
            <div className="w-6 shrink-0" />
          </div>
          {/* Establishment rows */}
          {estEntries.map((est) => (
            <EstablishmentRow key={`est-${est.company_id}`} company={est} plans={est.plans}
              isRTL={isRTL} isAdmin={isAdmin} compTxMap={compTxMap} smartFilter={smartFilter} />
          ))}
          {/* Employee rows - Sortable */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sorted.map(e => e.employee_id)} strategy={verticalListSortingStrategy}>
              {sorted.map((emp) => {
                const workflows = computeWorkflowTracking(emp.transactions);
                const empPlans = plansByEmp[emp.employee_id] || [];
                if (workflows.length === 0 && empPlans.length === 0) return null;
                return <TrackingRow key={emp.employee_id} emp={emp} workflows={workflows} plans={empPlans}
                  isRTL={isRTL} isAdmin={isAdmin} navigate={navigate} smartFilter={smartFilter}
                  onStatusChange={handleStatusChange} onUpdateActualPaid={handleUpdateActualPaid} onExecuteStep={handleExecuteStep} onCancelRemaining={handleCancelRemaining} onUpdateNote={handleUpdateNote} onDeletePlan={handleDeletePlan} />;
              })}
            </SortableContext>
          </DndContext>
        </Card>
      )}
    </div>
  );
};

export default Tracking;
